import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  status: string;
  monthly_quota: number;
  quota_used: number;
  quota_remaining: number;
  quota_reset_at: string;
}

export interface LoginResult {
  user: User;
  accessToken: string;
}

export const useAuthStore = defineStore('auth', () => {
  // Estado
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const quotaInfo = computed(() => ({
    used: user.value?.quota_used || 0,
    remaining: user.value?.quota_remaining || 0,
    total: user.value?.monthly_quota || 0,
    resetAt: user.value?.quota_reset_at || null
  }));

  // Actions
  const setAuth = (loginResult: LoginResult) => {
    user.value = loginResult.user;
    accessToken.value = loginResult.accessToken;
    localStorage.setItem('accessToken', loginResult.accessToken);
    localStorage.setItem('user', JSON.stringify(loginResult.user));
  };

  const clearAuth = () => {
    user.value = null;
    accessToken.value = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  };

  const loadFromStorage = () => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        accessToken.value = storedToken;
        user.value = JSON.parse(storedUser);
      } catch (error) {
        console.error('Error loading auth from storage:', error);
        clearAuth();
      }
    }
  };

  const login = async (email: string, password: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al iniciar sesión');
      }

      const loginResult: LoginResult = await response.json();
      setAuth(loginResult);
      return loginResult;
    } catch (err: any) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (email: string, password: string, name: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      console.log('🚀 Iniciando registro:', { email, name });
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ email, password, name }),
      });

      console.log('📡 Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        throw new Error(errorData.error || 'Error al registrar usuario');
      }

      const loginResult: LoginResult = await response.json();
      console.log('✅ Registro exitoso:', loginResult);
      setAuth(loginResult);
      return loginResult;
    } catch (err: any) {
      console.error('❌ Error en registro:', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchProfile = async () => {
    if (!accessToken.value) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          clearAuth();
          return;
        }
        throw new Error('Error al obtener perfil');
      }

      const profileData = await response.json();
      user.value = profileData.user;
      localStorage.setItem('user', JSON.stringify(profileData.user));
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      if (err.message.includes('401')) {
        clearAuth();
      }
    }
  };

  const logout = () => {
    clearAuth();
    // Redirigir al home y cerrar otras ventanas
    window.location.href = '/';
  };

  const handleOAuthCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const oauthError = urlParams.get('error');
    const redirect = urlParams.get('redirect') || '/';

    if (oauthError) {
      error.value = 'Error en autenticación OAuth';
      return;
    }

    if (token) {
      // Simular login con token OAuth
      const mockUser = {
        id: 'oauth-user',
        email: 'oauth@example.com',
        name: 'Usuario OAuth',
        role: 'user' as const,
        status: 'active',
        monthly_quota: 10,
        quota_used: 0,
        quota_remaining: 10,
        quota_reset_at: new Date().toISOString()
      };

      setAuth({ user: mockUser, accessToken: token });
      
      // Limpiar URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Redirigir
      window.location.href = redirect;
    }
  };

  const getAuthHeaders = () => {
    if (!accessToken.value) return {};
    return {
      'Authorization': `Bearer ${accessToken.value}`,
    };
  };

  // Inicializar desde localStorage
  loadFromStorage();

  return {
    // Estado
    user,
    accessToken,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    isAdmin,
    quotaInfo,
    
    // Actions
    setAuth,
    clearAuth,
    login,
    register,
    fetchProfile,
    logout,
    handleOAuthCallback,
    getAuthHeaders,
  };
});
